import { useCallback, useRef } from "react";
import { useAnalytics } from "./analytics-provider";
import type { TrackEvent } from "@salestrak/analytics-taxonomy";

/**
 * Wraps an Apollo mutation function so the analytics event fires only after
 * the mutation resolves — failed mutations are not counted as completed
 * actions. A mutation that resolves with a typed error payload also needs
 * `isSuccess` (see `payloadIs`), or the error counts as a completed action. The event is read through a ref so inline literals don't break
 * the returned function's identity.
 *
 * Generic over the function type, not over Apollo's TData/TVars pair: the
 * wrapper never inspects the options, and restating the mutate signature
 * here would couple it to Apollo's generics (which carry the declared
 * errorPolicy as a type parameter) for no gain.
 */
export function useTrackedMutation<TArgs extends unknown[], TResult>(
  mutationFn: (...args: TArgs) => Promise<TResult>,
  event: TrackEvent,
  isSuccess?: (result: NoInfer<TResult>) => boolean,
): (...args: TArgs) => Promise<TResult> {
  const { track } = useAnalytics();
  const eventRef = useRef(event);
  eventRef.current = event;
  const isSuccessRef = useRef(isSuccess);
  isSuccessRef.current = isSuccess;

  return useCallback(
    async (...args: TArgs) => {
      const result = await mutationFn(...args);
      if (isSuccessRef.current?.(result) ?? true) track(eventRef.current);
      return result;
    },
    [mutationFn, track],
  );
}

/**
 * The `isSuccess` for an Apollo mutation whose payload is a result union: the
 * mutation resolves with a typed error (`ValidationError`, `NotFoundError`)
 * as well as with the entity, so a resolved promise alone is not a success.
 * `field` is the mutation's root field, `typename` the success member.
 */
export function payloadIs(field: string, typename: string) {
  return (result: { data?: unknown }): boolean => {
    const data = result.data as Record<string, { __typename?: string } | null> | null | undefined;
    return data?.[field]?.__typename === typename;
  };
}
